import React, { useState, useEffect } from "react";
import { Stack, Input, FormLabel, Box, Image } from "@chakra-ui/react";
import ReusableDrawer from "components/Drawers/ReusableDrawer";
import GradientButton from "components/Buttons/GradientButton";
import ReusableInput from "components/Inputs/ReusableInput";
import { formatDate } from "utils/dateFormatter";
import CategorySelect from "./CategoryList";

const EditHelpTopicDrawer = ({ isOpen, onClose, helpTopic, onUpdateHelpTopic }) => {
  const [formData, setFormData] = useState({
    topic_id: "",
    title: "",
    description: "",
    category: "",
    image: null,
    created_at: "",
  });

  const [isEditing, setIsEditing] = useState(false);

  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const buildImageUrl = (baseUrl, path) => {
    if (!baseUrl || !path) return null;
    const cleanBase = baseUrl.replace(/\/$/, "");
    const cleanPath = String(path).replace(/^\/+/, "");
    return `${cleanBase}/${cleanPath}`;
  };

  useEffect(() => {
    if (helpTopic) {
      setFormData({
        ...helpTopic,
        title: helpTopic.title || "",
        description: helpTopic.description || "",
        category: helpTopic.category || "",
        image: helpTopic.image || null,
      });
      setIsEditing(false);
    }
  }, [helpTopic]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  
  const handleCategoryChange = (e) => {
    setFormData((prev) => ({ ...prev, category: e.target.value }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({ ...prev, image: e.target.files?.[0] || null }));
  };

  const handleToggleEdit = async () => {
    if (isEditing) {

      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title);
      formDataToSend.append("description", formData.description);
      formDataToSend.append("category", formData.category);


      if (formData.image instanceof File) {
        formDataToSend.append("image", formData.image);
      }

      const updatedTopic = await onUpdateHelpTopic(helpTopic.topic_id, formDataToSend);

      // If your action returns { success, data }
      if (updatedTopic?.success && updatedTopic?.data) {
        setFormData((prev) => ({
          ...prev,
          ...updatedTopic.data,
          category: updatedTopic.data.category || prev.category,
        }));
      }

      setIsEditing(false);
      onClose(); 
      return;
    }

    setIsEditing(true);
  };

  return (
    <ReusableDrawer
      isOpen={isOpen}
      onClose={() => {
        setIsEditing(false);
        onClose();
      }}
      headerContent={<h2>{isEditing ? "Edit Help Topic" : "View Help Topic"}</h2>}
      bodyContent={
        <Stack spacing={4}>
          <ReusableInput
            label="Title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="Enter topic title"
            isReadOnly={!isEditing}
          />

          <Box opacity={isEditing ? 1 : 0.7} pointerEvents={isEditing ? "auto" : "none"}>
            <CategorySelect value={formData.category} onChange={handleCategoryChange} />
          </Box>

          <ReusableInput
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Enter message or description"
            type="textarea"
            isReadOnly={!isEditing}
          />

          {/* Show current image if image is a string path and not editing */}
          {formData.image && typeof formData.image === "string" && !isEditing && (
            <Box mb={4}>
              <Image
                src={buildImageUrl(BASE_URL, formData.image)}
                alt={formData.title}
                maxW="100%"
              />
            </Box>
          )}

          <FormLabel>Image</FormLabel>
          <Input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            isDisabled={!isEditing}
          />

          <ReusableInput
            label="Date Added"
            name="dateAdded"
            value={formatDate(formData?.created_at) || ""}
            isReadOnly
            placeholder="Auto-generated"
          />
        </Stack>
      }
      footerContent={
        <Stack direction="row" spacing={4} justifyContent="flex-end">
          <GradientButton
            fontSize="15px"
            width="100px"
            height="40px"
            onClick={() => {
              setIsEditing(false);
              onClose();
            }}
          >
            Cancel
          </GradientButton>

          <GradientButton
            fontSize="15px"
            width="100px"
            height="40px"
            onClick={handleToggleEdit}
            isDisabled={
              isEditing && (!formData.title || !formData.description || !formData.category)
            }
          >
            {isEditing ? "Save" : "Edit"}
          </GradientButton>
        </Stack>
      }
    />
  );
};

export default EditHelpTopicDrawer;
